import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Box, Typography, IconButton, Dialog, Button, useMediaQuery, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { ACCENT_COLOR } from '../config/uiTheme';

const ACTIVE_COLOR = 'var(--hl-root)';
const INTRO_LINES = 3;

const stepCountOf = (section) => Math.max(1, section.lastStep - section.firstStep + 1);

const formatSteps = (count) => `${count} ${count === 1 ? 'step' : 'steps'}`;

// L'intro est coupee a 3 lignes. « More » n'apparait que si le texte deborde
// vraiment ; sans mesure (scrollHeight a 0, jsdom) on le montre par prudence.
const Intro = ({ text, expanded, onToggle }) => {
  const ref = useRef(null);
  const [fits, setFits] = useState(false);

  useLayoutEffect(() => {
    if (expanded) return;
    const el = ref.current;
    if (!el) return;
    setFits(el.scrollHeight > 0 && el.scrollHeight <= el.clientHeight + 1);
  }, [text, expanded]);

  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        ref={ref}
        data-testid="lesson-intro"
        sx={{
          fontSize: '0.9rem',
          lineHeight: 1.5,
          color: 'rgba(255,255,255,0.8)',
          whiteSpace: 'pre-line',
          ...(expanded ? {} : {
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: INTRO_LINES,
            WebkitBoxOrient: 'vertical'
          })
        }}
      >
        {text}
      </Typography>
      {(expanded || !fits) && (
        <Typography
          component="button"
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-label={expanded ? 'Show less of the intro' : 'Show more of the intro'}
          sx={{
            mt: 0.5,
            p: 0,
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            fontSize: '0.85rem',
            fontWeight: 600,
            color: ACCENT_COLOR
          }}
        >
          {expanded ? 'Less' : 'More'}
        </Typography>
      )}
    </Box>
  );
};

/**
 * LessonPanel - le panneau d'entree d'une lecon et sa liste des sections (un
 * seul composant pour les deux). Plein ecran sur telephone, portrait comme
 * paysage (la plus petite dimension sous 600 px, meme regle que le mode
 * compact de App) ; fenetre centree ailleurs. L'entete et le pied sont fixes,
 * l'intro et la liste defilent entre les deux.
 */
const LessonPanel = ({
  open,
  lesson,
  artist,
  sections,
  currentSectionIndex,
  onClose,
  onStart,
  onSelectSection,
  onWatchVideo
}) => {
  const theme = useTheme();
  const phone = useMediaQuery(
    `(max-width:${theme.breakpoints.values.sm - 0.05}px), (max-height:${theme.breakpoints.values.sm - 0.05}px)`
  );
  const [introExpanded, setIntroExpanded] = useState(false);

  // Chaque ouverture repart sur l'intro repliee a 3 lignes.
  useEffect(() => {
    if (open) setIntroExpanded(false);
  }, [open]);

  const description = (lesson.description || '').trim();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={phone}
      fullWidth
      maxWidth={false}
      aria-labelledby="lesson-panel-title"
      PaperProps={{
        sx: {
          backgroundColor: '#141420',
          backgroundImage: 'none',
          borderRadius: phone ? 0 : '16px',
          border: phone ? 'none' : '1px solid rgba(255,255,255,0.1)',
          m: phone ? 0 : 2,
          maxWidth: phone ? '100%' : 480,
          maxHeight: phone ? '100%' : '80vh',
          display: 'flex',
          flexDirection: 'column',
          pt: phone ? 'var(--safe-area-inset-top, env(safe-area-inset-top, 0px))' : 0,
          pb: phone ? 'var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))' : 0
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, px: 2.5, pt: 2, pb: 1.5, flexShrink: 0 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography id="lesson-panel-title" sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: 'white', lineHeight: 1.3 }}>
            {lesson.title}
          </Typography>
          {artist && (
            <Typography noWrap sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)', mt: 0.25 }}>
              {artist.name}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} aria-label="Close" sx={{ color: 'rgba(255,255,255,0.7)', mr: -1, mt: -0.75 }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2.5, pb: 1 }}>
        {description && (
          <Intro
            text={description}
            expanded={introExpanded}
            onToggle={() => setIntroExpanded((v) => !v)}
          />
        )}

        {lesson.videoUrl && (
          <Button
            onClick={onWatchVideo}
            aria-label="Watch the video"
            startIcon={<OndemandVideoIcon />}
            sx={{
              mb: 2.5,
              px: 2,
              py: 0.9,
              borderRadius: '18px',
              color: 'white',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.85rem',
              backgroundColor: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.15)',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.16)' }
            }}
          >
            Watch the video
          </Button>
        )}

        <Typography sx={{
          fontSize: '0.75rem',
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.55)',
          mb: 1
        }}>
          Sections
        </Typography>

        <Box role="list" aria-label="Lesson sections">
          {sections.map((section, i) => {
            const active = i === currentSectionIndex;
            return (
              <Box key={`${section.name}-${i}`} role="listitem" sx={{ mb: 0.5 }}>
                <Box
                  role="button"
                  tabIndex={0}
                  aria-current={active ? 'true' : undefined}
                  onClick={() => onSelectSection(i)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelectSection(i);
                    }
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 1.5,
                    py: 1.25,
                    borderRadius: '10px',
                    cursor: 'pointer',
                    backgroundColor: active ? 'rgba(var(--hl-root-rgb), 0.15)' : 'rgba(255,255,255,0.04)',
                    border: `1px solid ${active ? 'rgba(var(--hl-root-rgb), 0.5)' : 'transparent'}`,
                    '&:hover': { backgroundColor: active ? 'rgba(var(--hl-root-rgb), 0.2)' : 'rgba(255,255,255,0.08)' }
                  }}
                >
                  <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.45)', minWidth: 20, fontVariantNumeric: 'tabular-nums' }}>
                    {i + 1}
                  </Typography>
                  <Typography sx={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: '0.9rem',
                    fontWeight: active ? 'bold' : 500,
                    color: active ? ACTIVE_COLOR : 'white'
                  }}>
                    {section.name}
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.45)', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                    {formatSteps(stepCountOf(section))}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box sx={{
        flexShrink: 0,
        px: 2.5,
        pt: 1.5,
        pb: 2.5,
        borderTop: '1px solid rgba(255,255,255,0.08)'
      }}>
        <Button
          onClick={onStart}
          aria-label="Start"
          fullWidth
          endIcon={<PlayArrowIcon />}
          sx={{
            height: 44,
            borderRadius: '22px',
            color: '#000',
            fontWeight: 700,
            fontSize: '0.95rem',
            textTransform: 'none',
            backgroundColor: '#fff',
            boxShadow: 'none',
            '&:hover': { backgroundColor: '#f2f2f2', boxShadow: 'none' },
            '&:active': { backgroundColor: '#e5e5e5' }
          }}
        >
          Start
        </Button>
      </Box>
    </Dialog>
  );
};

export default LessonPanel;
